import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import {
    beginEnrollment,
    confirmEnrollment,
    MfaApiError,
    mfaErrorKey,
    resendEnrollmentCode,
    type MFACodeSent,
    type MFAConfirmResult,
    type MFAEnrollment,
} from '../../lib/mfa'

/**
 * Enrolls an e-mail or SMS factor (spec 2026-10-01-04): the account address
 * (e-mail) or a phone number (SMS) receives a first code, which confirms the
 * factor. Used by the forced-enrollment page and the Security tab.
 */
export function CodeFactorEnrollment({
    org,
    kind,
    email,
    onConfirmed,
}: {
    org: string
    kind: 'email' | 'sms'
    /** The account address, shown before the first code is sent. */
    email?: string | null
    onConfirmed: (result: MFAConfirmResult) => void
}) {
    const { t } = useTranslation('auth')
    const [phone, setPhone] = useState('')
    const [enrollment, setEnrollment] = useState<MFAEnrollment | null>(null)
    // The enrollment token carrying the latest code sent: nothing is stored
    // server-side until the confirmation.
    const [enrollmentToken, setEnrollmentToken] = useState('')
    const [sent, setSent] = useState<MFACodeSent | null>(null)
    const [code, setCode] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [cooldownUntil, setCooldownUntil] = useState(0)
    const [now, setNow] = useState(Date.now())
    const cooldown = Math.max(0, Math.ceil((cooldownUntil - now) / 1000))

    useEffect(() => {
        if (cooldownUntil <= Date.now()) return
        const id = setInterval(() => setNow(Date.now()), 1000)
        return () => clearInterval(id)
    }, [cooldownUntil])

    const fail = (err: unknown) => {
        if (err instanceof MfaApiError && err.retryAfter) {
            setCooldownUntil(Date.now() + err.retryAfter * 1000)
            setNow(Date.now())
        }
        setError(t(mfaErrorKey(err instanceof MfaApiError ? err.code : null)))
    }

    const codeWasSent = (result: MFACodeSent) => {
        setSent(result)
        setCooldownUntil(Date.now() + result.resend_after * 1000)
        setNow(Date.now())
    }

    const start = async (e?: React.FormEvent) => {
        e?.preventDefault()
        setBusy(true)
        setError(null)
        try {
            const result = await beginEnrollment(org, kind === 'sms' ? { kind, phone: phone.trim() } : { kind })
            setEnrollment(result)
            setEnrollmentToken(result.enrollment_token)
            if (result.code) codeWasSent(result.code)
        } catch (err) {
            fail(err)
        } finally {
            setBusy(false)
        }
    }

    const resend = async () => {
        if (!enrollment) return
        setBusy(true)
        setError(null)
        try {
            const result = await resendEnrollmentCode(org, kind, enrollmentToken)
            if (result.enrollment_token) setEnrollmentToken(result.enrollment_token)
            codeWasSent(result)
            setCode('')
        } catch (err) {
            fail(err)
        } finally {
            setBusy(false)
        }
    }

    const confirm = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!enrollment || !code.trim()) return
        setBusy(true)
        setError(null)
        try {
            onConfirmed(await confirmEnrollment(org, kind, enrollmentToken, code.trim()))
        } catch (err) {
            fail(err)
            setCode('')
        } finally {
            setBusy(false)
        }
    }

    if (!enrollment || !sent) {
        return (
            <form onSubmit={start} className="space-y-4" data-testid={`${kind}-enroll-start`}>
                <p className="text-sm text-muted-foreground">
                    {kind === 'email' ? t('mfa.setup.email_intro', { email: email ?? '' }) : t('mfa.setup.sms_intro')}
                </p>
                {kind === 'sms' && (
                    <div className="space-y-2">
                        <Label htmlFor="sms-phone">{t('mfa.setup.phone_label')}</Label>
                        <Input
                            id="sms-phone"
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+33 6 12 34 56 78"
                            data-testid="sms-phone-input"
                        />
                        <p className="text-xs text-muted-foreground">{t('mfa.setup.phone_hint')}</p>
                    </div>
                )}
                {error && <p className="text-sm text-destructive" data-testid={`${kind}-enroll-error`}>{error}</p>}
                <Button
                    type="submit"
                    disabled={busy || cooldown > 0 || (kind === 'sms' && !phone.trim())}
                    className="w-full"
                    data-testid={`${kind}-enroll-send`}
                >
                    {busy ? t('mfa.setup.starting') : cooldown > 0 ? t('mfa.challenge.resend_in', { seconds: cooldown }) : t('mfa.challenge.send_code')}
                </Button>
            </form>
        )
    }

    return (
        <form onSubmit={confirm} className="space-y-4" data-testid={`${kind}-enroll-confirm`}>
            <p className="text-sm text-muted-foreground" data-testid={`${kind}-enroll-sent`}>
                {t('mfa.challenge.code_sent', { destination: sent.destination })}
            </p>
            <div className="space-y-2">
                <Label htmlFor={`${kind}-code`}>{t('mfa.challenge.code_label')}</Label>
                <Input
                    id={`${kind}-code`}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    maxLength={7}
                    className="font-mono tracking-widest"
                    autoFocus
                    data-testid={`${kind}-code-input`}
                />
            </div>
            {error && <p className="text-sm text-destructive" data-testid={`${kind}-enroll-error`}>{error}</p>}
            <Button type="submit" disabled={busy || !code.trim()} className="w-full" data-testid={`${kind}-confirm-btn`}>
                {busy ? t('mfa.setup.confirming') : t('mfa.setup.confirm')}
            </Button>
            <button
                type="button"
                className="block w-full text-center text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:no-underline disabled:opacity-60"
                onClick={resend}
                disabled={busy || cooldown > 0}
                data-testid={`${kind}-resend`}
            >
                {cooldown > 0 ? t('mfa.challenge.resend_in', { seconds: cooldown }) : t('mfa.challenge.resend')}
            </button>
        </form>
    )
}
